import { queryClient, queryKeys } from '@/lib/query-client';
import type {
  AreasQuery,
  CreateAreaBody,
  CreateRegionBody,
  UpdateAreaBody,
  UpdateRegionBody,
} from '@skewb-works/contracts';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { locationsService } from '../api/locationsService';

// ---- Regions ----

export function useRegions() {
  return useQuery({
    queryKey: queryKeys.locations.regions(),
    queryFn: () => locationsService.listRegions(),
  });
}

export function useCreateRegion() {
  return useMutation({
    mutationFn: (data: CreateRegionBody) => locationsService.createRegion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
    },
    meta: { resourceName: 'Region', action: 'create' },
  });
}

export function useUpdateRegion(id: number) {
  return useMutation({
    mutationFn: (data: UpdateRegionBody) => locationsService.updateRegion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
    },
    meta: { resourceName: 'Region', action: 'update' },
  });
}

export function useDeleteRegion() {
  return useMutation({
    mutationFn: (id: number) => locationsService.deleteRegion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
    },
    meta: { resourceName: 'Region', action: 'delete' },
  });
}

// ---- Areas ----

export function useAreas(query: AreasQuery = {}) {
  return useQuery({
    queryKey: [...queryKeys.locations.areas(), query],
    queryFn: () => locationsService.listAreas(query),
    placeholderData: keepPreviousData,
  });
}

export function useCreateArea() {
  return useMutation({
    mutationFn: (data: CreateAreaBody) => locationsService.createArea(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
    },
    meta: { resourceName: 'Area', action: 'create' },
  });
}

export function useUpdateArea(id: number) {
  return useMutation({
    mutationFn: (data: UpdateAreaBody) => locationsService.updateArea(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
    },
    meta: { resourceName: 'Area', action: 'update' },
  });
}

export function useDeleteArea() {
  return useMutation({
    mutationFn: (id: number) => locationsService.deleteArea(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.areas() });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.regions() });
    },
    meta: { resourceName: 'Area', action: 'delete' },
  });
}
